<!-- Shortcuts toggle -->
<button #shortcutsOrigin mat-icon-button (click)="openPanel()">
  <mat-icon [svgIcon]="'heroicons_outline:squares-plus'" />
</button>

<!-- Shortcuts panel -->
<ng-template #shortcutsPanel>
  <div
    class="fixed inset-0 flex flex-col overflow-hidden shadow-lg sm:static sm:inset-auto sm:w-90 sm:min-w-90 sm:rounded-2xl"
  >
    <!-- Header -->
    <div class="flex shrink-0 items-center bg-primary py-4 pl-6 pr-4 text-on-primary">
      <div class="-ml-1 mr-3 sm:hidden">
        <button mat-icon-button (click)="closePanel()">
          <mat-icon class="text-current icon-size-5" [svgIcon]="'heroicons_solid:x-mark'" />
        </button>
      </div>
      <div class="flex items-center text-lg font-medium leading-10">
        <span class="">Shortcuts</span>
        <span *ngIf="mode !== 'view'" class="ml-1">
          <ng-container *ngIf="mode === 'add'">- Add new</ng-container>
          <ng-container *ngIf="mode === 'modify' || mode === 'edit'">- Editing</ng-container>
        </span>
      </div>
      <div class="ml-auto">
        <!-- View mode -->
        <ng-container *ngIf="mode === 'view'">
          <!-- Enter 'modify' mode -->
          <button mat-icon-button [matTooltip]="'Enter edit mode'" (click)="changeMode('modify')">
            <mat-icon class="text-current icon-size-5" [svgIcon]="'heroicons_solid:pencil-square'" />
          </button>
          <!-- Enter 'add' mode -->
          <button mat-icon-button [matTooltip]="'Add shortcut'" (click)="newShortcut()">
            <mat-icon class="text-current icon-size-5" [svgIcon]="'heroicons_solid:plus-circle'" />
          </button>
        </ng-container>

        <!-- Modify mode -->
        <ng-container *ngIf="mode === 'modify'">
          <!-- Enter 'view' mode -->
          <button mat-icon-button [matTooltip]="'Exit edit mode'" (click)="changeMode('view')">
            <mat-icon class="text-current icon-size-5" [svgIcon]="'heroicons_solid:check-circle'" />
          </button>
        </ng-container>

        <!-- Add mode -->
        <ng-container *ngIf="mode === 'add'">
          <!-- Enter 'view' mode -->
          <button mat-icon-button [matTooltip]="'Cancel'" (click)="changeMode('view')">
            <mat-icon class="text-current icon-size-5" [svgIcon]="'heroicons_solid:x-circle'" />
          </button>
        </ng-container>

        <!-- Edit mode -->
        <ng-container *ngIf="mode === 'edit'">
          <!-- Enter 'modify' mode -->
          <button mat-icon-button [matTooltip]="'Cancel'" (click)="changeMode('modify')">
            <mat-icon class="text-current icon-size-5" [svgIcon]="'heroicons_solid:x-circle'" />
          </button>
        </ng-container>
      </div>
    </div>
    <div class="bg-card relative -mb-px flex flex-auto flex-col overflow-y-auto sm:max-h-120">
      <!-- View mode -->
      <ng-container *ngIf="mode === 'view' || mode === 'modify'">
        <!-- Shortcuts -->
        <div class="grid grid-flow-row grid-cols-2">
          <!-- Shortcut -->
          <ng-container *ngFor="let shortcut of shortcuts; trackBy: trackByFn">
            <div
              class="group bg-card relative flex flex-col overflow-hidden border-b border-r even:border-r-0 hover:bg-gray-50 dark:hover:bg-black dark:hover:bg-opacity-5"
            >
              <div
                *ngIf="mode === 'modify'"
                aria-label="Edit shortcut"
                class="absolute inset-0 z-99 cursor-pointer"
                role="button"
                tabindex="0"
                (click)="editShortcut(shortcut)"
                (keyup.enter)="editShortcut(shortcut)"
              ></div>
              <!-- Normal links -->
              <a
                *ngIf="!shortcut.useRouter"
                class="flex h-full w-full flex-col items-center justify-center py-6 no-underline"
                [href]="shortcut.link"
                [ngClass]="{
                  'pointer-events-none': mode === 'modify',
                }"
              >
                <ng-container *ngTemplateOutlet="linkContent; context: { $implicit: shortcut }" />
              </a>
              <!-- Router links -->
              <a
                *ngIf="shortcut.useRouter"
                class="flex h-full w-full flex-col items-center justify-center py-6 no-underline"
                [ngClass]="{
                  'pointer-events-none': mode === 'modify',
                }"
                [routerLink]="shortcut.link"
              >
                <ng-container *ngTemplateOutlet="linkContent; context: { $implicit: shortcut }" />
              </a>
              <!-- Link content template -->
              <ng-template #linkContent let-shortcut>
                <div
                  class="relative mb-3 flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-gray-100 dark:bg-gray-700"
                >
                  <mat-icon
                    *ngIf="mode === 'modify'"
                    class="absolute z-20 opacity-0 icon-size-5 group-hover:opacity-100"
                    [svgIcon]="'heroicons_solid:pencil'"
                  />
                  <mat-icon
                    class="z-10"
                    [ngClass]="{
                      'group-hover:opacity-0': mode === 'modify',
                    }"
                    [svgIcon]="shortcut.icon"
                  />
                </div>
                <div class="text-center font-medium">
                  {{ shortcut.label }}
                </div>
                <div class="text-secondary text-center text-md">
                  {{ shortcut.description }}
                </div>
              </ng-template>
            </div>
          </ng-container>
        </div>

        <!-- No shortcuts -->
        <div
          *ngIf="!shortcuts || !shortcuts.length"
          class="flex flex-auto flex-col items-center justify-center px-8 py-12 sm:justify-start"
        >
          <div
            class="flex h-14 w-14 flex-0 items-center justify-center rounded-full bg-primary-100 dark:bg-primary-600"
          >
            <mat-icon class="text-primary-700 dark:text-primary-50" [svgIcon]="'heroicons_outline:bookmark'" />
          </div>
          <div class="mt-5 text-2xl font-semibold tracking-tight">No shortcuts</div>
          <div class="text-secondary mt-1 w-full max-w-60 text-center text-md">
            When you have shortcuts, they will appear here.
          </div>
        </div>
      </ng-container>

      <!-- Add/Edit mode -->
      <ng-container *ngIf="mode === 'add' || mode === 'edit'">
        <form autocomplete="new-password" class="p-6" [formGroup]="shortcutForm">
          <mat-form-field class="w-full">
            <mat-label>Label</mat-label>
            <input autocomplete="new-password" formControlName="label" matInput required />
          </mat-form-field>
          <mat-form-field class="w-full">
            <mat-label>Description</mat-label>
            <input autocomplete="new-password" formControlName="description" matInput />
          </mat-form-field>
          <mat-form-field class="w-full">
            <mat-label>Icon</mat-label>
            <input autocomplete="new-password" formControlName="icon" matInput required />
          </mat-form-field>
          <mat-form-field class="w-full">
            <mat-label>Link</mat-label>
            <input autocomplete="new-password" formControlName="link" matInput required />
          </mat-form-field>
          <mat-slide-toggle color="primary" formControlName="useRouter"> Use router for the link </mat-slide-toggle>
          <!-- Actions -->
          <div class="mt-4 flex items-center justify-end">
            <button *ngIf="mode === 'edit'" class="mr-2" mat-flat-button type="button" (click)="delete()">
              Delete
            </button>
            <button color="primary" mat-flat-button type="button" [disabled]="!shortcutForm.valid" (click)="save()">
              <ng-container *ngIf="mode === 'add'">Add</ng-container>
              <ng-container *ngIf="mode === 'edit'">Update</ng-container>
            </button>
          </div>
        </form>
      </ng-container>
    </div>
  </div>
</ng-template>
